/* eslint-disable no-restricted-globals */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useMemo } from 'react';
import type { SliderProps } from '@mui/material/Slider';
import MuiSlider from '@mui/material/Slider';

import { useField } from 'formik';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';

export interface TierInfo {
    color: string;
    textColor?: string;
}

interface StyledSliderProps {
    $tiers: TierInfo[];
    $totalSteps: number;
    $stepWidth: number;
    $steperSize: number;
    $gradient: string;
    $tier: number;
    $nullValue?: boolean;
}

const StyledSlider = styledComponent(MuiSlider)<SliderProps & StyledSliderProps>`
    position: relative;
    color: ${({ $tiers, $tier, $nullValue }) =>
        $nullValue ? colours.black.O24 : $tiers[Number($tier)]?.color} !important;
    width: ${({ $stepWidth }) => `${100 - $stepWidth}%`};
    left: ${({ $stepWidth }) => `${$stepWidth / 2}%`};
    .MuiSlider-rail {
        pointer-events: none;
        height: 1rem;
        width: ${({ $steperSize }) => `${100 + $steperSize}%`};
        left: ${({ $steperSize }) => `-${$steperSize / 2}%`};
        margin-top: -0.5rem;
        opacity: 1;
        border-radius: unset;
        ${({ $gradient }) => $gradient}
    }
    .MuiSlider-thumb {
        box-shadow: none;
        width: ${({ $steperSize }) => `${$steperSize}%`};
        height: 2rem;
        border-radius: 0.25rem;
        margin-top: -1rem;
        top: 75%;

        &::before {
            box-shadow: none;
        }
    }
    .MuiSlider-valueLabel {
        left: auto;
        top: 1.9rem;
        background: none;
        > span {
            background: none;
            transform: none;
            width: 3rem;
            text-align: center;
            > span {
                font-size: 0.8rem;
                transform: none;
                color: ${({ $tiers, $tier, $nullValue }) =>
                    $nullValue ? colours.white.main : $tiers[Number($tier)]?.textColor};
            }
        }
    }

    @media (max-width: ${breakpoints.tabletPortrait - 1}px) {
        width: 86%;
    }
`;

StyledSlider.defaultProps = {
    track: false,
    valueLabelDisplay: 'on',
};

const getSafeProgress = (receivedVal?: number) => (!isNaN(Number(receivedVal)) && receivedVal ? receivedVal : 0);

interface Props {
    name: string;
    tiers: TierInfo[];
    disabled?: boolean;
}

const SteppedTierSlider: React.FC<Props> = ({ name, tiers, disabled }) => {
    const [field, meta, helpers] = useField<string | undefined>({
        name,
    });
    const { setValue } = helpers;

    const nullValue = Boolean(!field.value);

    // Compute useful values from provided tiers
    const styleData = useMemo(() => {
        const totalSteps = tiers.length;
        const stepWidth = 100 / totalSteps;
        const steperSize = 100 / (totalSteps - 1);
        const gradient = `background: linear-gradient(to right, ${tiers
            .map((tier, i) => `${tier.color} ${stepWidth * i}%, ${tier.color} ${stepWidth * (i + 1)}%`)
            .join(', ')});`;
        return {
            totalSteps,
            stepWidth,
            steperSize,
            gradient,
        };
    }, [tiers]);

    const tierNumber = field.value ? field.value.charCodeAt(0) - 65 : 0;
    const percentProgress = tierNumber * styleData.steperSize;

    const getValuesFromSliderProgress = (progress: number) => {
        const tierNum = Math.floor(progress / styleData.steperSize);
        const tierLetter = String.fromCharCode(tierNum + 65);
        const tier = tiers[tierNum];
        return {
            tier,
            tierNumber: tierNum,
            tierLetter,
        };
    };

    const handleDrag = (_event: React.SyntheticEvent, sliderVal: number | number[]) => {
        const progress = getSafeProgress(Number(sliderVal));
        const { tierLetter } = getValuesFromSliderProgress(progress);
        if (field.value !== tierLetter) {
            setValue(tierLetter);
        }
    };

    const valueLabelFormat = (sliderVal?: number) => {
        const progress = getSafeProgress(sliderVal);
        const { tierLetter } = getValuesFromSliderProgress(progress);
        return field.value ? tierLetter : '?';
    };

    return (
        <StyledSlider
            key={nullValue.toString()} // Reset position when value becomes unknown ("?")
            // @ts-expect-error
            onChange={handleDrag}
            valueLabelFormat={valueLabelFormat}
            defaultValue={percentProgress}
            name={name}
            step={styleData.steperSize}
            $tiers={tiers}
            $tier={tierNumber}
            $stepWidth={styleData.stepWidth}
            $steperSize={styleData.steperSize}
            $gradient={styleData.gradient}
            $nullValue={nullValue}
            disabled={nullValue || disabled}
        />
    );
};

export default SteppedTierSlider;
